import {
    useCallback,
    useEffect,
    useReducer,
  } from "react";
  import toast from "react-hot-toast";
  import type { File } from "src/components/file-dropzone";
  import {
    GenericPath,
    useV3Create,
    useV3DeleteById,
    useV3DocumentDownload,
    useV3GenericGetData,
    useV3Update,
    useV3UploadDocument,
  } from "src/api/snugtotal/estate_app";
  import {
    allRoleTypesArray,
    downloadFileFromS3,
    rolesWithHierarchy,
  } from "src/utils/snug";
  import { useMounted } from "src/hooks/use-mounted";
  import {
    PeopleRole,
    UserDataObj,
  } from "src/types/snugtotal";
  import { slackHook } from "src/utils/snug-slack-hooks";
  import sortBy from "lodash/sortBy";
import useHousehold from "src/contexts/household-context";
  
  // Use lodash to sort the people roles based on allRoleTypesArray
  const sortPeopleByRoleUsingLodash = (
    people: PeopleRole[]
  ): PeopleRole[] => {
    // Create a map for quick lookup of index positions based on role
    const roleIndexMap = new Map(
      allRoleTypesArray.map((role, index) => [role, index])
    );
  
    return sortBy(people, [
      (person) => roleIndexMap.get(person.role || "") ?? Infinity,
    ]);
  };
  
  type PeopleRolesStoreAction =
    | { type: "SET_PEOPLE_ROLES"; payload: PeopleRole[] }
    | { type: "ADD_OR_UPDATE_PEOPLE_ROLES"; payload: PeopleRole }
    | { type: "DELETE_PEOPLE_ROLES"; payload: string }
    | { type: "SET_PEOPLE_ROLES_LOADING"; payload: boolean }
    | { type: "SET_PEOPLE_ROLES_SAVING_NEW"; payload: boolean }
    | { type: "SET_TARGET_ROLES"; payload: PeopleRole[] }
    | { type: "ADD_OR_UPDATE_TARGET_ROLES"; payload: PeopleRole }
    | { type: "SET_TARGET_ROLES_LOADING"; payload: boolean }
    | { type: "SET_TARGET_ROLES_SAVING_NEW"; payload: boolean }
    | { type: "SET_PEOPLE_ROLES_FILES_TO_UPLOAD"; payload: File[] }
    | { type: "SET_TARGET_USER_DATAS"; payload: UserDataObj[] }
    | { type: "SET_TARGET_USER_DATA_SAVING"; payload: boolean }
    | { type: "ADD_OR_UPDATE_TARGET_USER_DATA"; payload: UserDataObj }
    ;
  
  // Define reducer function for usePeopleRolesStore
  const peopleRolesStoreReducer = (
    state: UserDataContextStoreState,
    action: PeopleRolesStoreAction
  ): UserDataContextStoreState => {
    switch (action.type) {
      case "SET_PEOPLE_ROLES":
        const sortedPeopleRoles = sortPeopleByRoleUsingLodash(action.payload);
        return { ...state, peopleRoles: sortedPeopleRoles };
      case "ADD_OR_UPDATE_PEOPLE_ROLES":
        const peopleRoles = state.peopleRoles;
        const index = peopleRoles.findIndex((pr) => pr.id === action.payload.id);
        if (index > -1) {
          peopleRoles[index] = action.payload;
        } else {
          peopleRoles.push(action.payload);
        }
        return { ...state, peopleRoles };
      case "DELETE_PEOPLE_ROLES":
        return {
          ...state,
          peopleRoles: state.peopleRoles.filter((pr) => pr.id !== action.payload),
        };
      case "SET_PEOPLE_ROLES_LOADING":
        return { ...state, peopleRolesLoading: action.payload };
      case "SET_PEOPLE_ROLES_SAVING_NEW":
        return { ...state, peopleRolesSavingNew: action.payload };
      case "SET_PEOPLE_ROLES_FILES_TO_UPLOAD":
        return { ...state, peopleRolesFilestoUpload: action.payload };
      case "SET_TARGET_USER_DATA_SAVING":
        return { ...state, targetUserDataSaving: action.payload };
      case "SET_TARGET_USER_DATAS":
        return { ...state, targetUserDatas: action.payload };
      case "ADD_OR_UPDATE_TARGET_USER_DATA":
        const targetUserDatas = state.targetUserDatas;
        const targetUserDataIndex = targetUserDatas.findIndex(
          (ud) => ud.ud_id === action.payload.ud_id
        );
        if (targetUserDataIndex > -1) {
          targetUserDatas[targetUserDataIndex] = action.payload;
        } else {
          targetUserDatas.push(action.payload);
        }
        return { ...state, targetUserDatas };
  
      default:
        return state;
    }
  };
  
  export interface UserDataContexType {
    targetUserDatas: UserDataObj[];
    peopleRoles: PeopleRole[];
    peopleRolesLoading: boolean;
    peopleRolesSavingNew: boolean;
    peopleRolesFilestoUpload: File[];
    targetAuthRoles: PeopleRole[];
    targetAuthRolesLoading: boolean;
    targetAuthRolesSavingNew: boolean;
    handleRefreshPeopleRoles: () => void;
    handleAddOrUpdatePeopleRolesAndUserData: (
      ud_id: string | null,
      peopleRole: PeopleRole
    ) => Promise<PeopleRole | null>;
    handleAddOrUpdatePeopleRoleSingleCall: (
      ud_id: string | null,
      peopleRole: PeopleRole
    ) => Promise<void>;
    handleDeletePeopleRoles: (
      ud_id: string | null,
      id: string | null
    ) => Promise<void>;
    handleDeleteDocumentForPeopleRole: (
      peopleRoleId: string,
      document_id: string | null
    ) => Promise<void>;
    handleDownloadPeopleRolesDocument: (
      peopleRoleId: string,
      document_id: string | null
    ) => Promise<void>;
    handlePeopleRolesFileRemove: (file: File) => void;
    handlePeopleRolesFilesDrop: (newFiles: File[]) => void;
    handlePeopleRolesFilesRemoveAll: () => void;
    handleUploadPeopleRolesFiles: (
      ud_id: string | null,
      entityId: string
    ) => void;
    handleAddOrEditTargetUserData: (
      target_ud_id: string,
      userData: UserDataObj,
      isPartialUpdate?: boolean
    ) => Promise<UserDataObj | null>;
    sendHouseholdInviteEmail: (
      ud_id: string,
      household_id: string,
      target_ud_id: string
    ) => Promise<void>;
    handleAddToHousehold: (
      ud_id: string,
      household_id: string,
      target_ud_id: string,
      role: string,
    ) => Promise<void>;
    handleRemoveFromHousehold: (
      ud_id: string,
      household_id: string,
      target_ud_id: string
    ) => Promise<void>;
  }
  
  interface UserDataContextStoreState {
    peopleRoles: PeopleRole[];
    peopleRolesLoading: boolean;
    peopleRolesSavingNew: boolean;
    peopleRolesFilestoUpload: File[];
    targetUserDataSaving: boolean;
    targetUserDatas: UserDataObj[];
  }
  
  // The API only serializes a role's documents when asked (expand=documents).
  // Every write that replaces a role in the store asks, so an inline edit or an
  // upload never drops documents the drawer is showing. The list fetch asks
  // only for pages that show them; two dozen screens mount this store.
  const ROLE_WRITE_QUERY = { expand: "documents" };

  // Possible refactore would be to handle api selection here
  export const usePeopleRolesStore = (
    ud_id: string | null,
    options?: { withDocuments?: boolean }
  ) => {
    const withDocuments = Boolean(options?.withDocuments);
    const isMounted = useMounted();
    const { refreshHouseholds } = useHousehold();
  
    const [state, dispatch] = useReducer(peopleRolesStoreReducer, {
      peopleRoles: [],
      peopleRolesLoading: false,
      peopleRolesSavingNew: false,
      peopleRolesFilestoUpload: [],
      targetUserDataSaving: false,
      targetUserDatas: [],
    });
  
  
    const deleteById = useV3DeleteById(
      GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
    );
    const genericGetData = useV3GenericGetData<PeopleRole[]>(
      GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
    );
    const createPeopleRole = useV3Create<PeopleRole>(
      GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
    );
  
    const updatePeopleRole = useV3Update<PeopleRole>(
      GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
    );
    const downloadDocument = useV3DocumentDownload(
      GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
    );
    const uploadDocument = useV3UploadDocument<PeopleRole>(
      GenericPath.GENERIC_DOCUMENT_CREATE_V3
    );
    const deleteDocumentById = useV3DeleteById(
      GenericPath.GENERIC_DOCUMENT_DELETE_V3
    );
    const sendUserDataShareFromVerify = useV3Create<UserDataObj>(
      GenericPath.ADD_TO_HOUSEHOLD
    );

    const removeFromHousehold = useV3DeleteById(
      GenericPath.REMOVE_FROM_HOUSEHOLD
    );

    const sendHouseInviteEmail = useV3Create(
      GenericPath.INVITE_TO_HOUSEHOLD
    );
  
    const createUserData = useV3Create<UserDataObj>(
      GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
    );
    const updateUserData = useV3Update<UserDataObj>(
      GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
    );
  
    const handleAddOrEditTargetUserData = useCallback(
      async (
        target_ud_id: string,
        userData: UserDataObj,
        isPartialUpdate: boolean = false
      ): Promise<UserDataObj | null> => {
        if (!target_ud_id) {
          console.error("No user data id provided");
          return Promise.reject("No user data id provided");
        }
  
        const queryParams = {
          expand: "professional",
        }
        try {
          dispatch({ type: "SET_TARGET_USER_DATA_SAVING", payload: true });
          const params = { ud_id: target_ud_id, entity: "role-target-user-data" };
          if (userData.ud_id || isPartialUpdate) {
            const res = await updateUserData(
              { ...params, entity_id: userData.ud_id || "" },
              userData,
              isPartialUpdate,
              queryParams
            );
            dispatch({ type: "ADD_OR_UPDATE_TARGET_USER_DATA", payload: res });
            return res;
          } else {
            const res = await createUserData(params, userData, queryParams);
            dispatch({ type: "ADD_OR_UPDATE_TARGET_USER_DATA", payload: res });
            return res;
          }
        } catch (err) {
          console.error(err);
          throw err;
        } finally {
          dispatch({ type: "SET_TARGET_USER_DATA_SAVING", payload: false });
        }
      },
      [createUserData, updateUserData]
    );

    const handleAddOrEditRolesTargetUserData = useCallback(
      async (
        ud_id: string | null,
        targetUserData: UserDataObj | null
      ): Promise<UserDataObj | null> => {
        try {
          if (!targetUserData || !ud_id) {
            throw new Error("No target user data found");
          }
  
          if (targetUserData?.last_login) {
            //this is here so that UserData's attached to account are not updated.
            return Promise.resolve(targetUserData);
          }

          const queryParams = {
            expand: "professional",
          }
  
          dispatch({ type: "SET_TARGET_USER_DATA_SAVING", payload: true });
          const params = { ud_id: ud_id, entity: "role-target-user-data" };
          if (targetUserData.ud_id) {
            const res = await updateUserData(
              { ...params, entity_id: targetUserData.ud_id },
              targetUserData,
              true,
              queryParams
            );
            dispatch({ type: "ADD_OR_UPDATE_TARGET_USER_DATA", payload: res });
            return res;
          } else {
            const res = await createUserData(params, targetUserData, queryParams);
            dispatch({ type: "ADD_OR_UPDATE_TARGET_USER_DATA", payload: res });
            return res;
          }
        } catch (err) {
          console.error(err);
          return null;
          // throw err; Do not throw error. Just fail gracefully.
        } finally {
          dispatch({ type: "SET_TARGET_USER_DATA_SAVING", payload: false });
        }
      },
      [createUserData, updateUserData]
    );
  
    const handleDeleteTargetUserData = useCallback(
      (targetUserDataUdId: string) => {
        // get user data by id
        const targetUserData = state.targetUserDatas.find(
          (ud) => ud.ud_id === targetUserDataUdId
        );
  
        if (!targetUserData?.ud_id) {
          return Promise.reject("No id provided");
        }
  
        // check to see if the target user data id is connected to any people roles
        const peopleRole = state.peopleRoles.find(
          (pr) => pr.role_target_user_data_ud_id === targetUserData.ud_id
        );
  
        if (peopleRole) {
          console.info(
            "User Data connection has an id; thus it cannot be deleted."
          );
  
          return;
        }
        dispatch({
          type: "SET_TARGET_USER_DATAS",
          payload: state.targetUserDatas.filter(
            (ud) => ud.ud_id !== targetUserData.ud_id
          ),
        });
      },
      [state.targetUserDatas, state.peopleRoles]
    );
  
    const handlePeopleRolesFilesDrop = useCallback(
      (newFiles: File[]): void => {
        dispatch({
          type: "SET_PEOPLE_ROLES_FILES_TO_UPLOAD",
          payload: [...state.peopleRolesFilestoUpload, ...newFiles],
        });
      },
      [state.peopleRolesFilestoUpload]
    );
  
    const handlePeopleRolesFileRemove = useCallback(
      (file: File): void => {
        const updatedFiles = state.peopleRolesFilestoUpload.filter(
          (_file) => _file.path !== file.path
        );
        dispatch({
          type: "SET_PEOPLE_ROLES_FILES_TO_UPLOAD",
          payload: updatedFiles,
        });
      },
      [state.peopleRolesFilestoUpload]
    );
  
    const handlePeopleRolesFilesRemoveAll = useCallback((): void => {
      dispatch({ type: "SET_PEOPLE_ROLES_FILES_TO_UPLOAD", payload: [] });
    }, []);
  
    const handleUploadPeopleRolesFiles = useCallback(
      async (ud_id: string | null, peopleRoleId: string) => {
        if (!ud_id || !peopleRoleId) {
          console.error(
            `Missing required parameters ud_id: ${ud_id} peopleRoleId: ${peopleRoleId}`
          );
          return Promise.reject("Missing required parameters");
        }
        dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: true });
  
        const files = state.peopleRolesFilestoUpload;
        let remaining = files;
        const totalFiles = files.length;
        let successCount = 0;
        let failCount = 0;
        for (let index = 0; index < totalFiles; index++) {
          const currentFile = files[index];
  
          try {
            const res = await uploadDocument(
              {
                ud_id: ud_id,
                entity: "people-roles",
                entity_id: peopleRoleId,
              },
              currentFile,
              ROLE_WRITE_QUERY
            );
            if (res) {
              successCount++;
  
              // update state
              dispatch({
                type: "ADD_OR_UPDATE_PEOPLE_ROLES",
                payload: { ...res, role_target_user_data: null },
              });
  
              remaining = remaining.filter(
                (_file) => _file.path !== currentFile.path
              );
              dispatch({
                type: "SET_PEOPLE_ROLES_FILES_TO_UPLOAD",
                payload: remaining,
              });
  
              const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
              toast.success(successMessage);
            } else {
              failCount++;
              const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
              // Call your toast function here, e.g:
              toast.error(errorMessage);
              slackHook(
                `Document Error: Failed to upload document for entity_id: ${peopleRoleId} and document_id: ${currentFile.path}`
              );
            }
          } catch (error) {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
  
            console.error(errorMessage);
            slackHook(
              `Document Error: Failed to upload document for entity_id: ${peopleRoleId} and document_id: ${currentFile.path} with message "${error}"`
            );
          } finally {
            dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: false });
          }
        }
      },
      [uploadDocument, state.peopleRolesFilestoUpload]
    );
  
    const handleDownloadPeopleRolesDocument = useCallback(
      async (peopleRoleId: string, document_id: string | null) => {
        if (!document_id || !peopleRoleId || !ud_id) {
          return Promise.reject("Missing required parameters");
        }
        toast.success("Downloading file...");
  
        const params: any = {
          document_id: document_id,
          entity: "people-roles",
          entity_id: peopleRoleId,
          ud_id: ud_id,
        };
  
        downloadDocument(params)
          .then((res: any) => {
            if (res) {
              // download File from s3 res and get file name from mathcing document id in tax objects' documents array
              downloadFileFromS3(
                res,
                state.peopleRoles
                  ?.find((p) => p.id === peopleRoleId)
                  ?.documents?.find((d) => d.document_id === document_id)
                  ?.document_name || "Snug_Download.error"
              );
            } else {
              slackHook(
                `Document Error: Failed get document with message "No response"`
              );
            }
          })
          .catch((err) => {
            toast.error("Failed to download file");
            slackHook(
              `Document Error: Failed get document with message "${err}"`
            );
          });
      },
      [
        downloadDocument,
        state.peopleRoles,
        ud_id
      ]
    );
  
    const handleDeleteDocumentForPeopleRole = useCallback(
      async (peopleRoleId: string, document_id: string | null): Promise<void> => {
        if (!document_id || !peopleRoleId || !ud_id) {
          return Promise.reject("Missing required parameters");
        }
        toast.success("Removing the requested document.");
        try {
          dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: true });
  
          await deleteDocumentById({
            ud_id: ud_id,
            entity: "people-roles",
            entity_id: peopleRoleId,
            document_id: document_id,
          });
          if (isMounted()) {
            // remove the document object formm the document array in the tax object
            const updatedPeopleRoles = state.peopleRoles.map((p) => {
              if (p.id === peopleRoleId) {
                return {
                  ...p,
                  documents: p.documents
                    ? p.documents.filter((d) => d.document_id !== document_id)
                    : [],
                };
              }
              return p;
            });
            dispatch({
              type: "SET_PEOPLE_ROLES",
              payload: updatedPeopleRoles,
            });
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to delete document");
          throw err;
        } finally {
          if (isMounted()) {
            dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: false });
          }
        }
      },
      [
        isMounted,
        deleteDocumentById,
        state.peopleRoles,
        ud_id
      ]
    );
  
    const handleDeletePeopleRoles = useCallback(
      async (ud_id: string | null, id: string | null): Promise<void> => {
        if (!id || !ud_id) {
          return Promise.reject("No id provided");
        }
        try {
          // get people role by id, and get the people object from it.
          const peopleRole = state.peopleRoles.find((pr) => pr.id === id);
          if (!peopleRole) {
            throw new Error("People role not found");
          }
          dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: true });
          handleDeleteTargetUserData(
            peopleRole.role_target_user_data_ud_id || ""
          );
          const params = {
            ud_id: ud_id,
            entity: "people-roles",
            entity_id: id,
          };
          const queryParams = {
            expand: "role_target_user_data,role_target_user_data.professional"
          }
          const res = (await deleteById(params, queryParams)) as {
            data: PeopleRole[];
          };
          if (isMounted()) {
            if (rolesWithHierarchy.includes(peopleRole.role || "")) {
              // all three of thre hiearchy roles of the same "role" are in the response and all need to be replaced in state. ie, if a parent role of "EXECUTOR", which is in the rolesWithHiearchy array, is deleted, all children roles of "EXECUTOR" need to be deleted as well.
              // and replaced with "res" which is the response from the deleteById function.
              const allRelatedRolesRemoved = state.peopleRoles.filter(
                (pr) => pr.role !== peopleRole.role
              );
              dispatch({
                type: "SET_PEOPLE_ROLES",
                payload: [...allRelatedRolesRemoved, ...(res.data || [])],
              });
            } else {
              const updatedPeopleRoles = state.peopleRoles.filter(
                (p) => p.id !== id
              );
              dispatch({ type: "SET_PEOPLE_ROLES", payload: updatedPeopleRoles });
            }
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to remove this role!");
          throw err;
        } finally {
          if (isMounted()) {
            dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: false });
          }
        }
      },
      [isMounted, state.peopleRoles, deleteById, handleDeleteTargetUserData]
    );
  
    const handleGetPeopleRoles = useCallback(
      async (ud_id: string | null, signal: AbortSignal | null) => {
        if (!ud_id) {
          return;
        }
  
        const params = {
          ud_id: ud_id,
          entity: "people-roles",
        };
        try {
          dispatch({ type: "SET_PEOPLE_ROLES_LOADING", payload: true });
          const response = await genericGetData(
            params,
            {
              expand: [
                "role_target_user_data",
                "role_target_user_data.professional",
                ...(withDocuments ? ["documents"] : []),
              ].join(","),
              exclude_role: "FINANCIAL_PROFESSIONAL_CLIENT",
            },
            signal || undefined
          );
          if (isMounted()) {
            // get all unique target user data ids from the people roles and remove duplicates, nulls, and defineds and set into state
            const targetUserDatas = response
              .map((pr) => pr.role_target_user_data)
              .filter((ud) => ud !== null && ud !== undefined) as UserDataObj[];
            //now, the target user datas array will have duplictes, remove them to create a lisst of unqiue target user datas. can use ud_id as a unique identifier.
            const uniqueTargetUserDatas = targetUserDatas.filter(
              (ud, index, self) =>
                index ===
                self.findIndex((t) => t.ud_id === ud.ud_id || t === null)
            );
            dispatch({
              type: "SET_TARGET_USER_DATAS",
              payload: uniqueTargetUserDatas,
            });
  
            // remove target user data from all people roles and set into state
            const people_role_without_target_user_data = response.map((pr) => {
              const newy = { ...pr };
              delete newy.role_target_user_data;
              newy.role_target_user_data_ud_id = pr.role_target_user_data?.ud_id;
              return newy;
            });
            dispatch({
              type: "SET_PEOPLE_ROLES",
              payload: people_role_without_target_user_data,
            });
          }
        } catch (err) {
          console.error(err);
        } finally {
            dispatch({ type: "SET_PEOPLE_ROLES_LOADING", payload: false });
        }
      },
      [isMounted, genericGetData, withDocuments]
    );
  
    const handleRefreshPeopleRoles = useCallback(
      // this is just a wrapper for handleGetPeopleRoles function, calls that function with the current user data id as the parameter
      async () => {
        await handleGetPeopleRoles(
          ud_id || null,
          null
        );
      },
      [handleGetPeopleRoles, ud_id]
    );
  
    const handleAddToHousehold = useCallback(
      async (ud_id: string, household_id: string, target_ud_id: string, role: string) => {
        if (!ud_id || !household_id || !target_ud_id) {
          return;
        }

        const params = {
          ud_id: ud_id,
          entity: "household",
          entity_id: household_id,
          subcategory: "user-data",
          subcategory_id: target_ud_id,
        };
        try {
          // response is updated household so we need to update the state
          await sendUserDataShareFromVerify(params, { 
            role: role, 
          });
          refreshHouseholds();
        } catch (err) {
          console.error(err);
          toast.error("Failed to send email");
        }
      },
      [sendUserDataShareFromVerify, refreshHouseholds]
    );

    const handleRemoveFromHousehold = useCallback(
      async (ud_id: string, household_id: string, target_ud_id: string) => {
        if (!ud_id || !household_id || !target_ud_id) {
          return;
        }
        const params = {
          ud_id: ud_id,
          entity: "household",
          entity_id: household_id,
          subcategory: "user-data",
          subcategory_id: target_ud_id,
        };
        try {
          await removeFromHousehold(params);
          refreshHouseholds();
        } catch (err) {
          console.error(err);
          toast.error("Failed to send email");
        }
      },
      [removeFromHousehold, refreshHouseholds]
    );

    const sendHouseholdInviteEmail = useCallback(
      async (ud_id: string, household_id: string, target_ud_id: string) => {
        if (!ud_id || !household_id || !target_ud_id) {
          return;
        }
        const params = {
          ud_id: ud_id,
          entity: "household",
          entity_id: household_id,
          subcategory: "user-data",
          subcategory_id: target_ud_id,
        };
        try {
          await sendHouseInviteEmail(params, {});
          toast.success("Invite sent");
        } catch (err) {
          console.error(err);
          toast.error("Failed to send email");
        }
      },
      [sendHouseInviteEmail]
    );
  
    const handleAddOrUpdatePeopleRolesAndUserData = useCallback(
      async (
        ud_id: string | null,
        peopleRole: PeopleRole
      ): Promise<PeopleRole | null> => {
        if (
          !ud_id ||
          (!peopleRole.role_target_user_data &&
            !peopleRole.role_target_user_data_ud_id)
        ) {
          console.error("No user data id provided");
          return Promise.reject("No user data id provided");
        }
  
        try {
          dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: true });
          let targetUserData: UserDataObj | null =
            peopleRole.role_target_user_data || null;
          let res: PeopleRole | null;

          // A person another household member saved: the record exists but
          // none of our roles point at it yet. The contact endpoints only take
          // edits from someone already linked, so create our role first and
          // apply any contact edits after.
          const personUdId = peopleRole.role_target_user_data?.ud_id || null;
          const linkFirst =
            Boolean(personUdId) &&
            personUdId !== ud_id &&
            !peopleRole.id &&
            !state.peopleRoles.some(
              (pr) => pr.role_target_user_data_ud_id === personUdId
            );
          if (linkFirst) {
            const { role_target_user_data: person, ...roleFields } = peopleRole;
            const linked = await createPeopleRole(
              { ud_id: ud_id, entity: "people-roles" },
              { ...roleFields, role_target_user_data_ud_id: personUdId },
              ROLE_WRITE_QUERY
            );
            dispatch({
              type: "ADD_OR_UPDATE_PEOPLE_ROLES",
              payload: { ...linked, role_target_user_data: null },
            });
            const saved = await handleAddOrEditRolesTargetUserData(
              ud_id,
              person || null
            );
            dispatch({
              type: "ADD_OR_UPDATE_TARGET_USER_DATA",
              payload: saved || (person as UserDataObj),
            });
            return linked;
          }
          
          if (peopleRole.role_target_user_data) {
            if (peopleRole.role_target_user_data?.ud_id === ud_id) {
              //use update owner
              targetUserData = await handleAddOrEditTargetUserData(
                ud_id,
                peopleRole.role_target_user_data || null,
              );
            } else {
              targetUserData = await handleAddOrEditRolesTargetUserData(
                ud_id,
                peopleRole.role_target_user_data || null,
              );
            }
  
            if (targetUserData) {

              peopleRole.role_target_user_data_ud_id =
                targetUserData.ud_id || peopleRole.role_target_user_data_ud_id;
              delete peopleRole.role_target_user_data; //keep this clean.
  
              // set targetUserData into targetUserDatas state, replacing it if it exists or adding it if it doesn't
              dispatch({
                type: "ADD_OR_UPDATE_TARGET_USER_DATA",
                payload: targetUserData,
              });
            } else if (peopleRole.role_target_user_data) {
              delete peopleRole.role_target_user_data;
            } else {
              //if not target user data we're fucked here. throw error and report to frontend.
              throw new Error("User data not returned.");
            }
          }
  
          if (peopleRole.id) {
            const params = {
              ud_id: ud_id,
              entity: "people-roles",
              entity_id: peopleRole.id,
            };
            // remove field role_target_user_data
            res = await updatePeopleRole(
              params,
              {
                ...peopleRole,
                role_target_user_data_ud_id:
                  targetUserData?.ud_id ?? peopleRole.role_target_user_data_ud_id,
              },
              false,
              ROLE_WRITE_QUERY
            );
          } else if (!peopleRole.id) {
            const params = {
              ud_id: ud_id,
              entity: "people-roles",
            };
            res = await createPeopleRole(
              params,
              {
                ...peopleRole,
                role_target_user_data_ud_id:
                  targetUserData?.ud_id ?? peopleRole.role_target_user_data_ud_id,
              },
              ROLE_WRITE_QUERY
            );
          } else {
            res = null;
          }
  
          if (res?.id) {
            dispatch({
              type: "ADD_OR_UPDATE_PEOPLE_ROLES",
              payload: { ...res, role_target_user_data: null },
            });
            return res;
          }
  
          // add spouse to user data object if role is SPOUSE
  
          // weird workaround for temporary advisor client creation
          if (res) {
            return res;
          } else {
            return peopleRole;
          }
        } catch (err) {
          if (err.response && err.response.status === 400) {
            const data = err.response.data;
            // DRF field errors come back as { field: ["message"] } — show
            // them rather than guessing at the cause.
            const fieldErrors =
              data && typeof data === "object" && !data.details
                ? Object.entries(data)
                    .map(([field, msgs]) => `${field}: ${[msgs].flat().join(" ")}`)
                    .join("; ")
                : "";
            if (data && data.details) {
              toast.error(data.details, { duration: 10000 });
            } else if (fieldErrors) {
              toast.error(`Couldn't save: ${fieldErrors}`, { duration: 10000 });
            } else {
              toast.error(
                "Something went wrong! Check that your person is not a duplicate of another person in your dropdown and ensure the person being selected is over 18.",
                { duration: 10000 }
              );
            }
          } else {
            toast.error("Error adding your data! Please try again.");
          }
          throw err;
        } finally {
          dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: false });
        }
      },
      [
        createPeopleRole,
        updatePeopleRole,
        handleAddOrEditRolesTargetUserData,
        handleAddOrEditTargetUserData,
        state.peopleRoles,
      ]
    );

    // Beneficiaries can be 18+, so the generic "must be over 18" copy is wrong
    // for them — surface a beneficiary-appropriate message instead.
    const CUSTOM_PLAN_BENEFICIARY_ERROR =
      "Something went wrong! Please double check that this person isn't already saved as a beneficiary option.";
    const GENERIC_PEOPLE_ROLE_ERROR =
      "Something went wrong! Check that your person is not a duplicate of another person in your dropdown and ensure the person being selected is over 18.";

    const handleAddOrUpdatePeopleRoleSingleCall = useCallback(
      async (
        ud_id: string | null,
        peopleRole: PeopleRole
      ): Promise<void> => {
        if (
          !ud_id ||
          (!peopleRole.role_target_user_data &&
            !peopleRole.role_target_user_data_ud_id)
        ) {
          console.error("No user data id provided");
          return Promise.reject("No user data id provided");
        }
  
        try {
          dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: true });
          let res: PeopleRole | null;
  
          if (peopleRole.id) {
            const params = {
              ud_id: ud_id,
              entity: "people-roles",
              entity_id: peopleRole.id,
            };
            // remove field role_target_user_data
            res = await updatePeopleRole(
              params,
              {
                ...peopleRole,
                role_target_user_data_ud_id:
                  peopleRole?.role_target_user_data_ud_id ||
                  peopleRole?.role_target_user_data?.ud_id,
              },
              false,
              ROLE_WRITE_QUERY
            );
          } else {
            const params = {
              ud_id: ud_id,
              entity: "people-roles",
            };
            res = await createPeopleRole(
              params,
              {
                ...peopleRole,
                role_target_user_data_ud_id:
                  peopleRole?.role_target_user_data_ud_id ||
                  peopleRole?.role_target_user_data?.ud_id,
              },
              ROLE_WRITE_QUERY
            );
          }
  
          dispatch({
            type: "ADD_OR_UPDATE_PEOPLE_ROLES",
            payload: { ...res, role_target_user_data: null },
          });
        } catch (err) {
          if (err.response && err.response.status === 400) {
            if (err.response.data && err.response.data.details) {
              toast.error(err.response.data.details, { duration: 10000 });
            } else {
              toast.error(
                peopleRole.role === "CUSTOM_PLAN_BENEFICIARY"
                  ? CUSTOM_PLAN_BENEFICIARY_ERROR
                  : GENERIC_PEOPLE_ROLE_ERROR,
                { duration: 10000 }
              );
            }
          } else {
            toast.error("Error adding your data! Please try again.");
          }
          throw err;
        } finally {
          dispatch({ type: "SET_PEOPLE_ROLES_SAVING_NEW", payload: false });
        }
      },
      [
        createPeopleRole,
        updatePeopleRole,
      ]
    );
  
    useEffect(() => {
      const abortController = new AbortController();
      if (ud_id) {
        handleGetPeopleRoles(
          ud_id || null,
          abortController.signal
        );
      }
  
      return () => {
        abortController.abort();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      ud_id,
    ]);
  
    return {
      peopleRoles: state.peopleRoles,
      peopleRolesLoading: state.peopleRolesLoading,
      peopleRolesSavingNew: state.peopleRolesSavingNew,
      peopleRolesFilestoUpload: state.peopleRolesFilestoUpload,
      targetUserDatas: state.targetUserDatas,
      targetUserDataSaving: state.targetUserDataSaving,
      handleDeleteTargetUserData,
      handleAddOrUpdatePeopleRolesAndUserData,
      handleDeletePeopleRoles,
      handleDeleteDocumentForPeopleRole,
      handleDownloadPeopleRolesDocument,
      handlePeopleRolesFileRemove,
      handlePeopleRolesFilesDrop,
      handlePeopleRolesFilesRemoveAll,
      handleUploadPeopleRolesFiles,
      handleAddOrEditTargetUserData,
      handleAddOrUpdatePeopleRoleSingleCall,
      handleRefreshPeopleRoles,
      handleAddToHousehold,
      sendHouseholdInviteEmail,
      handleRemoveFromHousehold,
    };
  };
  